/**
 * Display-only, deterministic reordering of a score-sorted variant batch so that
 * near-identical variants of the same brand/series don't pile up in a visible run
 * at the top of category grids (e.g. a dozen Stressless "Laurel" colour variants
 * in a row, all with nearly identical quality scores and images).
 *
 * It never adds, removes or merges anything — it only defers a same-brand item past
 * a small cooldown window and pulls up the next different-brand item instead. The
 * same input always yields the same output, so SSR and offset pagination stay stable.
 * When a single brand dominates and no alternative is left, it degrades gracefully
 * back to pure score order.
 *
 * Applied per fetched batch (never across batch boundaries) so the offset pagination
 * stays intact and already-rendered tiles never jump when the next batch loads.
 */

const DEFAULT_COOLDOWN = 3; // min. distance (in tiles) before the same brand may repeat

type Brandable = { brand_slug?: string | null; brand_name?: string | null };

export function declusterByBrand<T extends Brandable>(
    items: T[],
    cooldown: number = DEFAULT_COOLDOWN,
): T[] {
    if (items.length <= cooldown + 1) return items;

    // brand_slug is the normalized key; brand_name is only a fallback and varies
    // in spelling ("wiho Küchen" vs "Wiho-Küchen"), so normalize it defensively.
    const brandOf = (it: T) =>
        it.brand_slug || (it.brand_name || "").toLowerCase().replace(/[^a-z0-9äöüß]/g, "");
    const remaining = items.slice();
    const result: T[] = [];
    const recent: string[] = []; // brands of the last `cooldown` placed tiles

    while (remaining.length > 0) {
        // Take the highest-score remaining item whose brand isn't in the cooldown
        // window; if every remaining item shares a recent brand, keep score order.
        let idx = remaining.findIndex((it) => !recent.includes(brandOf(it)));
        if (idx === -1) idx = 0;

        const [picked] = remaining.splice(idx, 1);
        result.push(picked);

        recent.push(brandOf(picked));
        if (recent.length > cooldown) recent.shift();
    }

    return result;
}
